import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { adminAPI } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon, 
  TrashIcon,
  PlusIcon,
  PencilSquareIcon,
  XMarkIcon,
  CheckIcon,
  MapPinIcon,
  ClockIcon,
  UserIcon,
  CubeIcon,
  PauseIcon,
  PlayIcon
} from '@heroicons/react/24/outline';

const AdminShipmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // Fetch shipment details
  const { data: shipmentData, isLoading } = useQuery({
    queryKey: ['admin-shipment', id],
    queryFn: () => adminAPI.getShipment(id),
  });

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: (statusData) => adminAPI.updateShipmentStatus(id, statusData),
    onSuccess: () => {
      toast.success('Status updated successfully!');
      queryClient.invalidateQueries(['admin-shipment', id]);
      setShowStatusForm(false);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update status');
    },
  });

  // Delete shipment mutation
  const deleteShipmentMutation = useMutation({
    mutationFn: () => adminAPI.deleteShipment(id),
    onSuccess: () => {
      toast.success('Shipment cancelled successfully!');
      navigate('/admin/shipments');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel shipment');
    },
  });

  // Hold shipment mutation
  const holdMutation = useMutation({
    mutationFn: ({ reason, holdType, facilityName }) => adminAPI.holdShipment(id, reason, holdType, facilityName),
    onSuccess: () => {
      toast.success('Shipment placed on customs hold');
      queryClient.invalidateQueries(['admin-shipment', id]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to place shipment on customs hold');
    }
  });

  // Release shipment mutation
  const releaseMutation = useMutation({
    mutationFn: () => adminAPI.releaseShipment(id),
    onSuccess: () => {
      toast.success('Shipment released from hold');
      queryClient.invalidateQueries(['admin-shipment', id]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to release shipment');
    }
  });

  // Edit tracking history entry mutation
  const updateTrackingMutation = useMutation({
    mutationFn: ({ historyId, eventData }) => adminAPI.updateTrackingEvent(id, historyId, eventData),
    onSuccess: () => {
      toast.success('Tracking entry updated!');
      queryClient.invalidateQueries(['admin-shipment', id]);
      setEditingEventId(null);
      setEditForm({});
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update tracking entry');
    },
  });

  // Delete tracking history entry mutation
  const deleteTrackingMutation = useMutation({
    mutationFn: (historyId) => adminAPI.deleteTrackingEvent(id, historyId),
    onSuccess: () => {
      toast.success('Tracking entry deleted!');
      queryClient.invalidateQueries(['admin-shipment', id]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete tracking entry');
    },
  });

  const startEditingEvent = (event) => {
    setEditingEventId(event.id);
    setEditForm({
      status: event.status,
      location: event.location || '',
      facilityName: event.facility_name || '',
      description: event.description || '',
      notes: event.notes || '',
      timestamp: event.timestamp ? format(new Date(event.timestamp), "yyyy-MM-dd'T'HH:mm") : ''
    });
  };

  const cancelEditingEvent = () => {
    setEditingEventId(null);
    setEditForm({});
  };

  const saveEditedEvent = (historyId) => {
    if (!editForm.status) {
      toast.error('Status is required');
      return;
    }
    updateTrackingMutation.mutate({
      historyId,
      eventData: {
        status: editForm.status,
        location: editForm.location,
        facilityName: editForm.facilityName,
        description: editForm.description,
        notes: editForm.notes,
        timestamp: editForm.timestamp ? new Date(editForm.timestamp).toISOString() : null
      }
    });
  };

  const handleDeleteEvent = (historyId) => {
    if (window.confirm('Are you sure you want to delete this tracking entry? This action cannot be undone.')) {
      deleteTrackingMutation.mutate(historyId);
    }
  };

  const onSubmitStatus = (data) => {
    updateStatusMutation.mutate(data);
  };

  const handleDeleteShipment = () => {
    if (window.confirm('Are you sure you want to cancel this shipment? This action cannot be undone.')) {
      deleteShipmentMutation.mutate();
    }
  };

  const handleHoldShipment = () => {
    const holdType = prompt('Select hold type:\n1. Customs Inspection\n2. Import Duties\n3. Missing Documentation\n4. Restricted Items\n5. Security Check\n\nEnter number (1-5):');
    if (holdType === null) return;

    const typeMap = {
      '1': 'customs_inspection',
      '2': 'import_duties',
      '3': 'missing_documentation',
      '4': 'restricted_items',
      '5': 'security_check'
    };

    const selectedType = typeMap[holdType];
    if (!selectedType) {
      toast.error('Invalid hold type selected');
      return;
    }

    const facilityName = prompt('Enter customs facility name (e.g., Istanbul Customs, JFK Customs):');
    if (facilityName === null) return;

    const reason = prompt('Enter additional details (optional):');
    if (reason === null) return;

    holdMutation.mutate({ reason: reason || '', holdType: selectedType, facilityName: facilityName || 'Customs Facility' });
  };

  const handleReleaseShipment = () => {
    releaseMutation.mutate();
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'exception', label: 'Exception' },
    { value: 'on_hold', label: 'On Hold' },
    { value: 'returned', label: 'Returned' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return '✅';
      case 'exception':
        return '⚠️';
      case 'cancelled':
      case 'returned':
        return '❌';
      case 'out_for_delivery':
        return '🚚';
      case 'in_transit':
        return '🚛';
      default:
        return '📦';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF6B00]"></div>
      </div>
    );
  }

  if (!shipmentData?.success) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0B1F3A] mb-2">Shipment Not Found</h2>
          <p className="text-[#64748B] mb-4">The requested shipment could not be found.</p>
          <button
            onClick={() => navigate('/admin/shipments')}
            className="btn btn-primary"
          >
            Back to Shipments
          </button>
        </div>
      </div>
    );
  }

  const { shipment, trackingHistory } = shipmentData.data;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/shipments')}
            className="flex items-center text-[#64748B] hover:text-[#0B1F3A] mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Shipments
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-[#0B1F3A]">
                Shipment #{shipment.tracking_number}
              </h1>
              <div className="flex items-center mt-2">
                <span className="text-2xl mr-2">{getStatusIcon(shipment.current_status)}</span>
                <span className={`status-badge status-${shipment.current_status} text-lg`}>
                  {shipment.current_status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <button
                onClick={() => setShowStatusForm(!showStatusForm)}
                className="btn btn-primary flex items-center space-x-2"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Update Status</span>
              </button>
              {shipment.current_status === 'on_hold' ? (
                <button
                  onClick={handleReleaseShipment}
                  disabled={releaseMutation.isLoading}
                  className="btn bg-[#16A34A] hover:bg-[#0f7d36] text-white flex items-center space-x-2 disabled:opacity-50"
                >
                  <PlayIcon className="h-5 w-5" />
                  <span>Release</span>
                </button>
              ) : (
                <button
                  onClick={handleHoldShipment}
                  disabled={holdMutation.isLoading}
                  className="btn bg-[#FF6B00] hover:bg-[#e65f00] text-white flex items-center space-x-2 disabled:opacity-50"
                >
                  <PauseIcon className="h-5 w-5" />
                  <span>Hold</span>
                </button>
              )}
              {shipment.current_status !== 'cancelled' && (
                <button
                  onClick={handleDeleteShipment}
                  className="btn bg-[#DC2626] hover:bg-[#b91d1d] text-white flex items-center space-x-2"
                >
                  <TrashIcon className="h-5 w-5" />
                  <span>Cancel</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Status Update Form */}
        {showStatusForm && (
          <div className="card mb-8">
            <h2 className="text-xl font-bold text-[#0B1F3A] mb-4">Update Tracking Status</h2>
            <form onSubmit={handleSubmit(onSubmitStatus)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    {...register('status', { required: 'Status is required' })}
                    className={`input ${errors.status ? 'border-red-500' : ''}`}
                  >
                    <option value="">Select status</option>
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.status && (
                    <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    {...register('location')}
                    className="input"
                    placeholder="Chicago Distribution Center"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facility Name
                  </label>
                  <input
                    type="text"
                    {...register('facilityName')}
                    className="input"
                    placeholder="Chicago Hub"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Delivery
                  </label>
                  <input
                    type="date"
                    {...register('estimatedDelivery')}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Progress Percentage (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  {...register('progressPercentage', { min: 0, max: 100 })}
                  className="input"
                  placeholder={shipment.progress_percentage || 0}
                />
                <p className="mt-1 text-xs text-gray-500">Update shipment progress based on current location (0% = source, 100% = destination)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  rows={3}
                  {...register('description')}
                  className="input"
                  placeholder="Package has arrived at facility and is being processed..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Internal)
                </label>
                <textarea
                  rows={2}
                  {...register('notes')}
                  className="input"
                  placeholder="Internal notes for staff..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowStatusForm(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateStatusMutation.isLoading}
                  className="btn btn-primary"
                >
                  {updateStatusMutation.isLoading ? 'Updating...' : 'Update Status'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Shipment Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* On Hold Alert */}
            {shipment.current_status === 'on_hold' && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <span className="text-2xl">⚠️</span>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-bold text-red-800">Customs Hold - International Shipment</h3>
                    <div className="mt-2 text-red-700">
                      <p className="font-medium">This package has been held by customs authorities.</p>
                      {trackingHistory.find(h => h.status === 'on_hold')?.description && (
                        <p className="mt-2 text-base font-semibold">{trackingHistory.find(h => h.status === 'on_hold').description}</p>
                      )}
                      {trackingHistory.find(h => h.status === 'on_hold')?.facility_name && (
                        <p className="mt-2 text-base text-red-800">
                          <strong>Customs Facility:</strong> {trackingHistory.find(h => h.status === 'on_hold').facility_name}
                        </p>
                      )}
                      {(() => {
                        const holdEvent = trackingHistory.find(h => h.status === 'on_hold');
                        const notes = holdEvent?.notes;
                        let displayNotes = '';
                        
                        if (notes) {
                          if (typeof notes === 'string') {
                            displayNotes = notes;
                          } else if (typeof notes === 'object') {
                            displayNotes = JSON.stringify(notes);
                          } else {
                            displayNotes = String(notes);
                          }
                        }
                        
                        if (displayNotes && displayNotes.trim()) {
                          return (
                            <div className="mt-3 p-3 bg-red-100 rounded-lg">
                              <p className="font-semibold text-red-900">Additional Details:</p>
                              <p className="text-red-800 text-base mt-1">{displayNotes}</p>
                            </div>
                          );
                        }
                        return null;
                      })()}
                      <p className="mt-3 text-sm text-red-600 italic">
                        This is an urgent message to you to contact us for further details to release your package from customs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Current Status */}
            <div className="card">
              <h2 className="text-xl font-bold text-[#0B1F3A] mb-4">Current Status</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-center space-x-3">
                  <MapPinIcon className="h-6 w-6 text-gray-400" />
                  <div>
                    <p className="text-sm text-[#64748B]">Current Location</p>
                    <p className="font-medium">{shipment.current_location || 'In Transit'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <ClockIcon className="h-6 w-6 text-gray-400" />
                  <div>
                    <p className="text-sm text-[#64748B]">Last Updated</p>
                    <p className="font-medium">
                      {format(new Date(shipment.updated_at), 'MMM dd, yyyy h:mm a')}
                    </p>
                  </div>
                </div>
                {!!shipment.estimated_delivery && (
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-6 w-6 text-gray-400" />
                    <div>
                      <p className="text-sm text-[#64748B]">Estimated Delivery</p>
                      <p className="font-medium">
                        {format(new Date(shipment.estimated_delivery), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                )}
                {!!shipment.actual_delivery && (
                  <div className="flex items-center space-x-3">
                    <ClockIcon className="h-6 w-6 text-[#16A34A]" />
                    <div>
                      <p className="text-sm text-[#64748B]">Delivered On</p>
                      <p className="font-medium text-[#16A34A]">
                        {format(new Date(shipment.actual_delivery), 'MMM dd, yyyy h:mm a')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sender & Recipient */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="text-lg font-semibold text-[#0B1F3A] mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2" />
                  Sender
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="font-medium">{shipment.sender_name}</p>
                  {shipment.sender_email && <p className="text-[#64748B]">{shipment.sender_email}</p>}
                  {shipment.sender_phone && <p className="text-[#64748B]">{shipment.sender_phone}</p>}
                  <p className="text-[#64748B]">
                    {shipment.sender_address}<br />
                    {[shipment.sender_city, shipment.sender_state, shipment.sender_postal_code].filter(Boolean).join(', ')}<br />
                    {shipment.sender_country}
                  </p>
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-semibold text-[#0B1F3A] mb-4 flex items-center">
                  <UserIcon className="h-5 w-5 mr-2" />
                  Recipient
                </h3>
                <div className="space-y-2 text-sm">
                  <p className="font-medium">{shipment.recipient_name}</p>
                  {shipment.recipient_email && <p className="text-[#64748B]">{shipment.recipient_email}</p>}
                  {shipment.recipient_phone && <p className="text-[#64748B]">{shipment.recipient_phone}</p>}
                  <p className="text-[#64748B]">
                    {shipment.recipient_address}<br />
                    {[shipment.recipient_city, shipment.recipient_state, shipment.recipient_postal_code].filter(Boolean).join(', ')}<br />
                    {shipment.recipient_country}
                  </p>
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="card">
              <h3 className="text-lg font-semibold text-[#0B1F3A] mb-4 flex items-center">
                <CubeIcon className="h-5 w-5 mr-2" />
                Package Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                {!!shipment.package_description && (
                  <div>
                    <p className="text-[#64748B]">Description</p>
                    <p className="font-medium">{shipment.package_description}</p>
                  </div>
                )}
                {!!shipment.package_weight && (
                  <div>
                    <p className="text-[#64748B]">Weight</p>
                    <p className="font-medium">{shipment.package_weight} kg</p>
                  </div>
                )}
                {!!shipment.package_dimensions && (
                  <div>
                    <p className="text-[#64748B]">Dimensions</p>
                    <p className="font-medium">{shipment.package_dimensions}</p>
                  </div>
                )}
                <div>
                  <p className="text-[#64748B]">Service Type</p>
                  <p className="font-medium capitalize">{shipment.service_type}</p>
                </div>
                {!!shipment.package_value && (
                  <div>
                    <p className="text-[#64748B]">Declared Value</p>
                    <p className="font-medium">${shipment.package_value}</p>
                  </div>
                )}
                <div>
                  <p className="text-[#64748B]">Special Options</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {!!shipment.signature_required && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-[#2563EB]">
                        Signature Required
                      </span>
                    )}
                    {!!shipment.fragile && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-[#FF6B00]">
                        Fragile
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {!!shipment.special_instructions && (
                <div className="mt-4">
                  <p className="text-[#64748B] text-sm">Special Instructions</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg mt-1">{shipment.special_instructions}</p>
                </div>
              )}
            </div>
          </div>

          {/* Tracking History */}
          <div className="lg:col-span-1">
            <div className="card">
              <h3 className="text-lg font-semibold text-[#0B1F3A] mb-6">Tracking History</h3>
              
              <div className="flow-root">
                <ul className="-mb-8">
                  {trackingHistory.map((event, index) => (
                    <li key={event.id || index}>
                      <div className="relative pb-8">
                        {index !== trackingHistory.length - 1 && (
                          <span
                            className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                            aria-hidden="true"
                          />
                        )}
                        <div className="relative flex space-x-3">
                          <div>
                            <span className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center ring-8 ring-white text-sm">
                              {getStatusIcon(event.status)}
                            </span>
                          </div>
                          {editingEventId === event.id ? (
                            /* Inline Edit Form */
                            <div className="flex-1 min-w-0 bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2.5">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                                <select
                                  value={editForm.status}
                                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                  className="input text-sm py-1.5"
                                >
                                  {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                                <input
                                  type="text"
                                  value={editForm.location}
                                  onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                                  className="input text-sm py-1.5"
                                  placeholder="e.g. Hamburg, Germany"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Facility Name</label>
                                <input
                                  type="text"
                                  value={editForm.facilityName}
                                  onChange={(e) => setEditForm({ ...editForm, facilityName: e.target.value })}
                                  className="input text-sm py-1.5"
                                  placeholder="e.g. HHLA Container Terminal"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time</label>
                                <input
                                  type="datetime-local"
                                  value={editForm.timestamp}
                                  onChange={(e) => setEditForm({ ...editForm, timestamp: e.target.value })}
                                  className="input text-sm py-1.5"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Description (Comments)</label>
                                <textarea
                                  rows={2}
                                  value={editForm.description}
                                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                  className="input text-sm py-1.5"
                                  placeholder="Visible to client on tracking page"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Notes (Internal)</label>
                                <textarea
                                  rows={2}
                                  value={editForm.notes}
                                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                  className="input text-sm py-1.5"
                                  placeholder="Internal notes for staff"
                                />
                              </div>
                              <div className="flex justify-end gap-2 pt-1">
                                <button
                                  type="button"
                                  onClick={cancelEditingEvent}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveEditedEvent(event.id)}
                                  disabled={updateTrackingMutation.isLoading}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-[#FF6B00] hover:bg-[#e65f00] rounded-lg transition-colors disabled:opacity-50"
                                >
                                  <CheckIcon className="h-4 w-4" />
                                  {updateTrackingMutation.isLoading ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex-1 min-w-0 group">
                              <div className="flex items-start justify-between">
                                <div>
                                  <div className="text-sm">
                                    <span className={`status-badge status-${event.status}`}>
                                      {event.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </span>
                                  </div>
                                  <p className="mt-0.5 text-sm text-gray-500">
                                    {format(new Date(event.timestamp), 'MMM dd, yyyy h:mm a')}
                                  </p>
                                </div>
                                <div className="flex gap-1 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => startEditingEvent(event)}
                                    title="Edit this entry"
                                    className="p-1.5 text-gray-400 hover:text-[#FF6B00] hover:bg-orange-50 rounded-lg transition-colors"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteEvent(event.id)}
                                    title="Delete this entry"
                                    className="p-1.5 text-gray-400 hover:text-[#DC2626] hover:bg-red-50 rounded-lg transition-colors"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                              {event.location && (
                                <div className="mt-2 text-sm text-gray-700">
                                  <p className="font-medium">{event.location}</p>
                                </div>
                              )}
                              {event.description && (
                                <div className="mt-2 text-sm text-gray-700">
                                  <p>{event.description}</p>
                                </div>
                              )}
                              {event.facility_name && (
                                <p className="mt-1 text-xs text-gray-500">
                                  Facility: {event.facility_name}
                                </p>
                              )}
                              {event.updated_by_name && (
                                <p className="mt-1 text-xs text-gray-500">
                                  Updated by: {event.updated_by_name}
                                </p>
                              )}
                              {event.notes && (
                                <p className="mt-1 text-xs text-gray-600 italic">
                                  {event.notes}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminShipmentDetail;
